import { useEffect, useRef, useState } from "react";
import Home from "./pages/Home";
import PlatformServicePage from "./pages/PlatformServicePage";
import ProductionServicePage from "./pages/ProductionServicePage";
import NotFoundPage from "./pages/NotFoundPage";
import redirects from "./content/redirects.json";

export type RouteMetadata = {
  title: string;
  description: string;
};

export const defaultMetadata: RouteMetadata = {
  title: "Ebrahim Ahmadivarzaneh — AWS Platform Consultant",
  description:
    "Independent AWS consulting by Ebrahim Ahmadivarzaneh at Ebi Consulting Oy: platform modernization, hybrid networking, production reliability, and cloud cost.",
};

export const routeMetadata: Record<string, RouteMetadata> = {
  "/": defaultMetadata,
  "/aws-platform-consulting": {
    title: "AWS Architecture & Engineering — Ebrahim Ahmadivarzaneh",
    description:
      "AWS architecture and hands-on engineering for founders and engineering teams: containers, networking, infrastructure cost, observability and migrations.",
  },
  "/aws-observability-consulting": {
    title: "Production Troubleshooting — Ebrahim Ahmadivarzaneh",
    description:
      "Hands-on production troubleshooting across infrastructure, databases and applications. Understand undocumented systems, build visibility and resolve causes with your engineers.",
  },
};

// Retired services remain reachable through their consolidated offering.
export const routeRedirects: Record<string, string> = redirects;

export const notFoundMetadata: RouteMetadata = {
  title: "Page not found — Ebi Consulting",
  description: "This page is unavailable. Find Ebrahim Ahmadivarzaneh’s current consulting services or get in touch.",
};

function redirectFor(path: string) {
  return Object.hasOwn(routeRedirects, path) ? routeRedirects[path] : undefined;
}

function activePath(path: string) {
  return redirectFor(path)?.split("#")[0] ?? path;
}

function normalizedPath() {
  // Called during prerendering too, where there is no window.
  if (typeof window === "undefined") return "/";
  const path = window.location.pathname.replace(/\/index\.html$/, "").replace(/\/+$/, "");
  return path || "/";
}

function renderRoute(path: string) {
  switch (activePath(path)) {
    case "/":
      return <Home />;
    case "/aws-platform-consulting":
      return <PlatformServicePage />;
    case "/aws-observability-consulting":
      return <ProductionServicePage />;
    default:
      return <NotFoundPage />;
  }
}

export default function App({ path: initialPath }: { path?: string }) {
  const [path, setPath] = useState(() => initialPath ?? normalizedPath());
  const initialPageView = useRef(true);

  useEffect(() => {
    const redirect = redirectFor(path);
    if (!redirect) return;
    const destination = new URL(redirect, window.location.origin);
    destination.search = window.location.search;
    window.history.replaceState({}, "", `${destination.pathname}${destination.search}${destination.hash}`);
    setPath(destination.pathname);
    window.requestAnimationFrame(() => {
      document.getElementById(destination.hash.slice(1))?.scrollIntoView({ behavior: "instant", block: "start" });
    });
  }, [path]);

  useEffect(() => {
    const onPopState = () => setPath(normalizedPath());
    const onDocumentClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const target = event.target as Element | null;
      const anchor = target?.closest<HTMLAnchorElement>("a[href]");
      if (!anchor || anchor.target || anchor.download) return;

      const destination = new URL(anchor.href, window.location.href);
      if (destination.origin !== window.location.origin) return;

      const nextPath = destination.pathname.replace(/\/+$/, "") || "/";
      if (!Object.hasOwn(routeMetadata, nextPath) && !redirectFor(nextPath)) return;

      event.preventDefault();
      window.history.pushState({}, "", `${destination.pathname}${destination.search}${destination.hash}`);
      setPath(nextPath);

      window.requestAnimationFrame(() => {
        if (destination.hash) {
          document.getElementById(destination.hash.slice(1))?.scrollIntoView();
        } else {
          window.scrollTo({ top: 0, behavior: "auto" });
        }
      });
    };

    window.addEventListener("popstate", onPopState);
    document.addEventListener("click", onDocumentClick);
    return () => {
      window.removeEventListener("popstate", onPopState);
      document.removeEventListener("click", onDocumentClick);
    };
  }, []);

  useEffect(() => {
    const currentPath = activePath(path);
    const isKnown = Object.hasOwn(routeMetadata, currentPath);
    const metadata = isKnown ? routeMetadata[currentPath] : notFoundMetadata;
    document.title = metadata.title;
    const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    description?.setAttribute("content", metadata.description);

    let robots = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.name = "robots";
      document.head.append(robots);
    }
    robots.content = isKnown ? "index,follow,max-image-preview:large" : "noindex,follow";

    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (isKnown) {
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.rel = "canonical";
        document.head.append(canonical);
      }
      canonical.href = `https://ebiconsulting.fi${currentPath}`;
    } else {
      canonical?.remove();
    }

    if (initialPageView.current) {
      initialPageView.current = false;
      return;
    }

    const analyticsWindow = window as typeof window & {
      plausible?: (
        eventName: string,
        options?: { u?: string },
      ) => void;
    };
    analyticsWindow.plausible?.("pageview", { u: window.location.href });
  }, [path]);

  return renderRoute(path);
}
